/**
 * Image Assets
 * Centralized asset management for the landing page
 */

// Logo assets
export const logoVector = "/assets/logo-vector.svg";
export const logoVector2 = "/assets/logo-vector-2.svg";

// Background patterns
export const patterns = "/assets/patterns.svg";

// Icons
export const iconPlus = "/assets/icon-plus.svg";
export const iconMinus = "/assets/icon-minus.svg";

// Why Partner with Evangelist section images (full resolution 800x480)
import image115Src from "@/assets/images/image-115.png?format=webp&quality=85";
import imgReportingSrc from "@/assets/images/img-reporting.png?format=webp&quality=85";
import imgBrandSafetySrc from "@/assets/images/img-brand-safety.png?format=webp&quality=85";

export const image115 = image115Src;
export const imgReporting = imgReportingSrc;
export const imgBrandSafety = imgBrandSafetySrc;

// For Clipping Platforms page assets
export const discordIcon = "/assets/discord-icon.svg";

// Official SVG wordmarks, reused across the landing page at every breakpoint.
import landingVyroSrc from "@/assets/images/landing/vyro.svg";
import landingContentRewardsSrc from "@/assets/images/landing/content-rewards.svg";
export const landingVyro = landingVyroSrc;
export const landingContentRewards = landingContentRewardsSrc;

// Campaign-distribution partner wordmarks and published-clip platform badges, exported from the
// landing Figma. SuperClip is a raster wordmark in the design file, so it is resized to 2x of the
// 219px the hero rail draws it at — its widest consumer, the distribution tile scales the same
// file down to 167px.
import landingFixatedSrc from "@/assets/images/landing/fixated.svg";
import landingSuperclipSrc from "@/assets/images/landing/superclip.png?w=438&format=webp&quality=90";
import landingShortsSrc from "@/assets/images/landing/youtube-shorts.svg";
import landingInstagramSrc from "@/assets/images/landing/instagram-reels.svg";
import landingTiktokSrc from "@/assets/images/landing/tiktok.svg";
import landingXSrc from "@/assets/images/landing/x.svg";
import landingSnapchatSrc from "@/assets/images/landing/snapchat.svg";
export const landingFixated = landingFixatedSrc;
export const landingSuperclip = landingSuperclipSrc;
export const landingShorts = landingShortsSrc;
export const landingInstagram = landingInstagramSrc;
export const landingTiktok = landingTiktokSrc;
export const landingX = landingXSrc;
export const landingSnapchat = landingSnapchatSrc;

// Hero-rail wordmarks with no vector original: both brands publish only raster artwork, so these
// are their own white-on-transparent logos trimmed to the ink and resized to 2x their rail width.
import landingDaylightSrc from "@/assets/images/landing/daylight.png?w=344&format=webp&quality=90";
import landingQcodeSrc from "@/assets/images/landing/qcode.png?w=314&format=webp&quality=90";
export const landingDaylight = landingDaylightSrc;
export const landingQcode = landingQcodeSrc;

// Workflow-step and brand-safety-severity icons, exported from the landing Figma as SVG. The step
// icons carry the design's 48px box, so every step draws at one optical size.
import landingStepStrategySrc from "@/assets/images/landing/icons/step-strategy.svg";
import landingStepSourceSrc from "@/assets/images/landing/icons/step-source.svg";
import landingStepPublishSrc from "@/assets/images/landing/icons/step-publish.svg";
import landingStepVerifySrc from "@/assets/images/landing/icons/step-verify.svg";
import landingStepPayoutSrc from "@/assets/images/landing/icons/step-payout.svg";
import landingStepMeasureSrc from "@/assets/images/landing/icons/step-measure.svg";
import landingSeverityLowSrc from "@/assets/images/landing/icons/sev-low.svg";
import landingSeverityMediumSrc from "@/assets/images/landing/icons/sev-medium.svg";
import landingSeverityHighSrc from "@/assets/images/landing/icons/sev-high.svg";

export const landingStepStrategy = landingStepStrategySrc;
export const landingStepSource = landingStepSourceSrc;
export const landingStepPublish = landingStepPublishSrc;
export const landingStepVerify = landingStepVerifySrc;
export const landingStepPayout = landingStepPayoutSrc;
export const landingStepMeasure = landingStepMeasureSrc;
export const landingSeverityLow = landingSeverityLowSrc;
export const landingSeverityMedium = landingSeverityMediumSrc;
export const landingSeverityHigh = landingSeverityHighSrc;

// Marks for the three "ways to work with Evangelist" cards, in the same 48px box as the step icons.
import landingServiceSelfServeSrc from "@/assets/images/landing/icons/service-self-serve.svg";
import landingServiceManagedSrc from "@/assets/images/landing/icons/service-managed.svg";
import landingServiceVerificationSrc from "@/assets/images/landing/icons/service-verification.svg";

export const landingServiceSelfServe = landingServiceSelfServeSrc;
export const landingServiceManaged = landingServiceManagedSrc;
export const landingServiceVerification = landingServiceVerificationSrc;

// White 40px glyphs for the cross-marketplace diagram's campaign-brief and workflow tiles.
import landingFlowDollarSrc from "@/assets/images/landing/icons/flow-dollar.svg";
import landingFlowTargetSrc from "@/assets/images/landing/icons/flow-target-arrow.svg";
import landingFlowUsersSrc from "@/assets/images/landing/icons/flow-users.svg";
import landingFlowVideoSrc from "@/assets/images/landing/icons/flow-video.svg";
import landingFlowFoldersSrc from "@/assets/images/landing/icons/flow-folders.svg";
import landingFlowRouteSrc from "@/assets/images/landing/icons/flow-route.svg";
import landingFlowShieldSrc from "@/assets/images/landing/icons/flow-shield-half.svg";
import landingFlowChartSrc from "@/assets/images/landing/icons/flow-chart-bar.svg";

export const landingFlowDollar = landingFlowDollarSrc;
export const landingFlowTarget = landingFlowTargetSrc;
export const landingFlowUsers = landingFlowUsersSrc;
export const landingFlowVideo = landingFlowVideoSrc;
export const landingFlowFolders = landingFlowFoldersSrc;
export const landingFlowRoute = landingFlowRouteSrc;
export const landingFlowShield = landingFlowShieldSrc;
export const landingFlowChart = landingFlowChartSrc;

// The hero's blurred colour field, rendered once from the Figma "bg color / blue" vector (its
// displacement and 120px blur filters are too heavy to leave to every browser). The workflow card
// in the cross-marketplace diagram reuses it scaled down, as the design does.
import landingHeroBackgroundSrc from "@/assets/images/landing/hero-background.png?format=webp&quality=85";
export const landingHeroBackground = landingHeroBackgroundSrc;

// The hero's three reporting cards, exported separately from the landing Figma so desktop and
// phone can arrange them differently (phones overlap them more tightly). Resized to 2x of the
// widest each is drawn; their corners are transparent, so the shadow comes from CSS.
import landingHeroDailyViewsSrc from "@/assets/images/landing/hero-daily-views.png?w=880&format=webp&quality=90";
import landingHeroCreatorPlatformsSrc from "@/assets/images/landing/hero-creator-platforms.png?w=768&format=webp&quality=90";
import landingHeroCountriesSrc from "@/assets/images/landing/hero-countries.png?w=630&format=webp&quality=90";

export const landingHeroDailyViews = landingHeroDailyViewsSrc;
export const landingHeroCreatorPlatforms = landingHeroCreatorPlatformsSrc;
export const landingHeroCountries = landingHeroCountriesSrc;

// Reporting screenshots for the platform tabs, exported from the landing Figma at 2x with the
// cards' shadows baked in. No `w=`: resizing could only throw detail away. Lossless, because lossy
// WebP breaks the soft alpha of those shadows into visible grain.
import landingPlatformVerificationSrc from "@/assets/images/landing/platform-view-verification.png?format=webp&lossless";
import landingPlatformAntiBottingSrc from "@/assets/images/landing/platform-anti-botting.png?format=webp&lossless";
import landingPlatformBrandSafetySrc from "@/assets/images/landing/platform-brand-safety.png?format=webp&lossless";
import landingPlatformInsightsSrc from "@/assets/images/landing/platform-campaign-insights.png?format=webp&lossless";

export const landingPlatformVerification = landingPlatformVerificationSrc;
export const landingPlatformAntiBotting = landingPlatformAntiBottingSrc;
export const landingPlatformBrandSafety = landingPlatformBrandSafetySrc;
export const landingPlatformInsights = landingPlatformInsightsSrc;

// Blog author photos, keyed by the `author` name in the article registry. 2x of the 40px byline.
import authorDanielCervenkovSrc from "@/assets/images/people/daniel-cervenkov.jpeg?w=80&format=webp&quality=85";
import authorMatthewHrabkovskySrc from "@/assets/images/people/matthew-hrabkovsky.jpeg?w=80&format=webp&quality=85";

export const authorPhotos: Readonly<Record<string, string>> = {
  "Daniel Cervenkov": authorDanielCervenkovSrc,
  "Matthew Hrabkovsky": authorMatthewHrabkovskySrc,
};

// Footer compliance/security logos
import footerLogo1Src from "@/assets/images/footer-logo-1.png?w=80&format=webp&quality=85";
import footerLogo2Src from "@/assets/images/footer-logo-2.png?w=80&format=webp&quality=85";

export const footerLogo1 = footerLogo1Src;
export const footerLogo2 = footerLogo2Src;

// Anti-botting article figures. The plots go through imagetools to WebP; no `w=` because both
// sources are already narrower than 2x their ~712px display width, so resizing would only soften
// them. The diagrams stay vector SVG — imagetools is raster-only and a vector diagram rasterized
// to WebP would lose exactly the label crispness these figures depend on.
import antibottingArchitectureSrc from "@/assets/images/article-antibotting/fraud-detection-architecture.svg";
import antibottingTrajectoriesSrc from "@/assets/images/article-antibotting/view-count-trajectories.png?format=webp&quality=90";
import antibottingDetectionPerformanceSrc from "@/assets/images/article-antibotting/detection-performance.png?format=webp&quality=90";
import antibottingVerificationSrc from "@/assets/images/article-antibotting/verification-comparison.svg";
import antibottingCoverageMatrixSrc from "@/assets/images/article-antibotting/two-test-coverage-matrix.svg";

export const antibottingArchitecture = antibottingArchitectureSrc;
export const antibottingTrajectories = antibottingTrajectoriesSrc;
export const antibottingDetectionPerformance = antibottingDetectionPerformanceSrc;
export const antibottingVerification = antibottingVerificationSrc;
export const antibottingCoverageMatrix = antibottingCoverageMatrixSrc;

// Brand-safety article figures. Same split as above: diagrams and the benchmark chart stay vector;
// the product screenshots are 2x captures shown at half size, so they need no `w=`. The spectrogram
// is a raster plot, resized to 2x its 680px display width.
import brandSafetyTolerancesSrc from "@/assets/images/article-brand-safety/brand-safety-tolerances.png?format=webp&quality=90";
import brandSafetyVisualMatchSrc from "@/assets/images/article-brand-safety/visual-match-robustness.svg";
import brandSafetySpectrogramSrc from "@/assets/images/article-brand-safety/acoustic-constellation-spectrogram.png?w=1360&format=webp&quality=90";
import brandSafetyBriefAlignmentSrc from "@/assets/images/article-brand-safety/brief-alignment-verification.svg";
import brandSafetyFlaggedSubmissionSrc from "@/assets/images/article-brand-safety/flagged-submission.png?format=webp&quality=90";
import brandSafetyArchitectureSrc from "@/assets/images/article-brand-safety/brand-safety-architecture.svg";

export const brandSafetyTolerances = brandSafetyTolerancesSrc;
export const brandSafetyVisualMatch = brandSafetyVisualMatchSrc;
export const brandSafetySpectrogram = brandSafetySpectrogramSrc;
export const brandSafetyBriefAlignment = brandSafetyBriefAlignmentSrc;
export const brandSafetyFlaggedSubmission = brandSafetyFlaggedSubmissionSrc;
export const brandSafetyArchitecture = brandSafetyArchitectureSrc;

// Future-of-clipping article figures, both raster and resized to 2x their display width: the
// histogram is drawn at 840px, the botting-group photo at 480px.
import futureOfClippingBottedShareSrc from "@/assets/images/article-future-of-clipping/botted-share-of-campaign-views.png?w=1680&format=webp&quality=90";
import futureOfClippingBottingGroupSrc from "@/assets/images/article-future-of-clipping/botting-group-chat.jpg?w=960&format=webp&quality=85";

export const futureOfClippingBottedShare = futureOfClippingBottedShareSrc;
export const futureOfClippingBottingGroup = futureOfClippingBottingGroupSrc;
